import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const PurchaseInvoiceList = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewPO, setPreviewPO] = useState(null);
  const [searchPONo, setSearchPONo] = useState('');
  const [searchSupplier, setSearchSupplier] = useState('');
  const [searchSite, setSearchSite] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculatePOTotal = (po) => {
    const lines = po.plines || po.items || [];
    if (po.total) return parseFloat(po.total);
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const subtotal = quantity * unitPrice;
      const afterDiscount = subtotal - discount;
      const vat = afterDiscount * (vatPercent / 100);
      const total = afterDiscount + vat;
      return sum + total;
    }, 0);
  };

  const calculatePOVAT = (po) => {
    const lines = po.plines || po.items || [];
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      const vat = afterDiscount * (vatPercent / 100);
      return sum + vat;
    }, 0);
  };

  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/pos`);
        const payload = response.data;
        
        console.log('API Response payload:', payload);
        
        let poList = [];
        
        // Handle paginated response: { data: { data: [...], pagination info }, message: "..." }
        if (payload && payload.data && payload.data.data && Array.isArray(payload.data.data)) {
          poList = payload.data.data;
        }
        // Handle { data: [...] } structure
        else if (payload && payload.data && Array.isArray(payload.data)) {
          poList = payload.data;
        }
        // Handle direct array structure
        else if (Array.isArray(payload)) {
          poList = payload;
        }
        
        console.log('Parsed POs list:', poList);
        console.log('POs count:', poList.length);
        setPurchaseOrders(poList);
        // Reset to first page when new data loads
        setCurrentPage(1);
      } catch (err) {
        console.error('Error fetching purchase invoices:', err);
        setError('Unable to load purchase invoices. Please try again later.');
        setPurchaseOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrders();
  }, [apiUrl]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Paid: 'badge-success',
      Unpaid: 'badge-warning',
      Pending: 'badge-info',
      Overdue: 'badge-error',
    };
    return `badge ${statusClasses[status] || 'badge-ghost'} badge-sm`;
  };

  // Filter purchase invoices based on search and status
  const filteredPOs = (Array.isArray(purchaseOrders) ? purchaseOrders : []).filter(po => {
    const poNo = (po.po_no || po.poNo || '').toLowerCase();
    const supplierName = (po.supplier?.name || po.supplierName || '').toLowerCase();
    const siteName = (po.customer?.name || '').toLowerCase();
    const status = po.status || '';

    const matchesPONo = searchPONo === '' || poNo.includes(searchPONo.toLowerCase());
    const matchesSupplier = searchSupplier === '' || supplierName.includes(searchSupplier.toLowerCase());
    const matchesSite = searchSite === '' || siteName.includes(searchSite.toLowerCase());
    const matchesStatus = statusFilter === 'All' || status === statusFilter;

    return matchesPONo && matchesSupplier && matchesSite && matchesStatus;
  });

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPOs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPOs.length / itemsPerPage);

  // Calculate GRAND TOTALS for ALL filtered records (not just current page)
  const grandTotalAmount = filteredPOs.reduce((sum, po) => sum + calculatePOTotal(po), 0);
  const grandTotalVAT = filteredPOs.reduce((sum, po) => sum + calculatePOVAT(po), 0);
  
  // Calculate current page totals
  const currentPageTotalAmount = currentItems.reduce((sum, po) => sum + calculatePOTotal(po), 0);
  const currentPageTotalVAT = currentItems.reduce((sum, po) => sum + calculatePOVAT(po), 0);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchPONo, searchSupplier, searchSite, statusFilter]);

  // Debug logging
  React.useEffect(() => {
    console.log('=== DEBUG INFO ===');
    console.log('Total Purchase Invoices:', purchaseOrders.length);
    console.log('Filtered POs:', filteredPOs.length);
    console.log('Current Page:', currentPage);
    console.log('Items per page:', itemsPerPage);
    console.log('Total Pages:', totalPages);
    console.log('Current Items:', currentItems.length);
    console.log('Search PO No:', searchPONo);
    console.log('Search Supplier:', searchSupplier);
    console.log('Search Site:', searchSite);
    console.log('Status Filter:', statusFilter);
    console.log('Grand Total Amount:', grandTotalAmount);
    console.log('Grand Total VAT:', grandTotalVAT);
    console.log('==================');
  }, [purchaseOrders, filteredPOs, searchPONo, searchSupplier, searchSite, statusFilter, currentPage, totalPages, currentItems.length, grandTotalAmount, grandTotalVAT]);

  const handleDelete = async (poId) => {
    if (window.confirm('Are you sure you want to delete this purchase invoice?')) {
      try {
        await axios.delete(`${apiUrl}/api/pos/${poId}`);
        setPurchaseOrders(purchaseOrders.filter(po => po.id !== poId));
      } catch (err) {
        console.error('Error deleting PO:', err);
        setError('Failed to delete purchase invoice. Please try again.');
      }
    }
  };

  const handleImagePreview = (imageUrl) => {
    if (imageUrl) {
      if (apiUrl.startsWith('http://')) {
        setImagePreviewUrl(imageUrl);
      } else {
        setImagePreviewUrl(`${apiUrl}/public/storage/${imageUrl}`);
      }
    }
  };

  const handleImageDownload = (imageUrl) => {
    if (imageUrl) {
      const fullUrl = apiUrl.startsWith('http://') 
        ? `${apiUrl}/storage/${imageUrl}` 
        : `${apiUrl}/public/storage/${imageUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const poPdfStyles = StyleSheet.create({
    page: {
      padding: 10,
      fontSize: 8,
      fontFamily: 'Helvetica',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      borderBottomWidth: 2,
      borderBottomColor: '#0891b2',
      paddingBottom: 20,
    },
    companyName: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#0891b2',
    },
    companyDetails: {
      fontSize: 10,
      color: '#666',
      marginTop: 5,
    },
    poTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
    subtitle: {
      fontSize: 12,
      color: '#666',
      textAlign: 'right',
      marginTop: 5,
    },
    infoSection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      backgroundColor: '#f8f9fa',
      padding: 5,
      borderRadius: 5,
    },
    infoBlock: {
      flex: 1,
    },
    infoLabel: {
      fontSize: 10,
      color: '#666',
      marginBottom: 3,
      textTransform: 'uppercase',
    },
    infoValue: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#333',
    },
    supplierSection: {
      marginBottom: 30,
      padding: 15,
      backgroundColor: '#f8f9fa',
      borderRadius: 5,
    },
    table: {
      marginBottom: 20,
      borderWidth: 1,
      borderColor: '#ddd',
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#0891b2',
      color: 'white',
      padding: 10,
      fontWeight: 'bold',
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#ddd',
      padding: 8,
    },
    tableRowAlt: {
      backgroundColor: '#f9f9f9',
    },
    col1: { width: '28%', paddingHorizontal: 5 },
    col2: { width: '9%', textAlign: 'center', paddingHorizontal: 5 },
    col3: { width: '11%', textAlign: 'right', paddingHorizontal: 5 },
    col4: { width: '13%', textAlign: 'right', paddingHorizontal: 5 },
    col5: { width: '8%', textAlign: 'center', paddingHorizontal: 5 },
    col6: { width: '13%', textAlign: 'right', paddingHorizontal: 5 },
    col7: { width: '13%', textAlign: 'right', paddingHorizontal: 5 },
    totals: {
      marginTop: 20,
      alignItems: 'flex-end',
    },
    totalRow: {
      flexDirection: 'row',
      padding: 5,
      width: '100%',
    },
    totalLabel: {
      flex: 1,
      textAlign: 'right',
      marginRight: 10,
      fontSize: 11,
    },
    totalValue: {
      width: 100,
      textAlign: 'right',
      fontSize: 11,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
    },
    summaryBox: {
      width: '48%',
      padding: 10,
      backgroundColor: '#f0f9fc',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: '#0891b2',
    },
    summaryLabel: {
      fontSize: 10,
      color: '#666',
      marginBottom: 5,
      fontWeight: 'bold',
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
    },
    grandTotal: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#0891b2',
      borderTopWidth: 2,
      borderTopColor: '#0891b2',
      paddingTop: 10,
      marginTop: 10,
      width: '100%',
    },
    notes: {
      marginTop: 30,
      padding: 15,
      backgroundColor: '#f8f9fa',
      borderRadius: 5,
    },
    notesTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      marginBottom: 8,
      color: '#0891b2',
    },
    notesText: {
      fontSize: 10,
      lineHeight: 1.4,
      color: '#666',
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      textAlign: 'center',
      color: '#666',
      fontSize: 9,
      borderTopWidth: 1,
      borderTopColor: '#ddd',
      paddingTop: 10,
    },
    compliance: {
      fontSize: 8,
      color: '#0891b2',
      marginTop: 5,
    },
  });

  const formatCurrencyPdf = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const POPDFDocument = ({ po }) => {
    const lines = po.plines || po.items || [];
    const supplierName = po.supplier?.name || po.supplierName || 'Unknown';
    const poDate = po.po_date || po.date;
    const total = calculatePOTotal(po);
    const subtotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      return sum + quantity * unitPrice;
    }, 0);
    const totalDiscount = lines.reduce((sum, line) => {
      return sum + parseFloat(line.discount_amount || line.discountAmount || 0);
    }, 0);
    const taxableAmount = lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      return sum + quantity * unitPrice - discount;
    }, 0);
    const vatTotal = lines.reduce((sum, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      return sum + afterDiscount * (vatPercent / 100);
    }, 0);

    return (
      <Document>
        <Page size="A4" style={poPdfStyles.page}>
          <View style={poPdfStyles.header}>
            <View>
              <Text style={poPdfStyles.companyName}>Diamond Quality Constructions</Text>
              <Text style={poPdfStyles.companyDetails}>Sharja, UAE</Text>
              <Text style={poPdfStyles.companyDetails}>TRN: 104836477000001</Text>
            </View>
            <View>
              <Text style={poPdfStyles.poTitle}>PURCHASE INVOICE</Text>
              <Text style={poPdfStyles.subtitle}>UAE Federal Tax Authority</Text>
            </View>
          </View>

          <View style={poPdfStyles.infoSection}>
            <View style={poPdfStyles.infoBlock}>
              <Text style={poPdfStyles.infoLabel}>PO Number</Text>
              <Text style={poPdfStyles.infoValue}>{po.po_no || po.poNo}</Text>
            </View>
            <View style={poPdfStyles.infoBlock}>
              <Text style={poPdfStyles.infoLabel}>PO Date</Text>
              <Text style={poPdfStyles.infoValue}>{formatDate(poDate)}</Text>
            </View>
            <View style={poPdfStyles.infoBlock}>
              <Text style={poPdfStyles.infoLabel}>Payment Status</Text>
              <Text style={poPdfStyles.infoValue}>{po.status}</Text>
            </View>
          </View>

          <View style={poPdfStyles.supplierSection}>
            <Text style={poPdfStyles.infoLabel}>Supplier:</Text>
            <Text style={[poPdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}>{supplierName}</Text>
            {po.supplier?.address || po.supplierAddress ? (
              <Text style={poPdfStyles.companyDetails}>{po.supplier?.address || po.supplierAddress}</Text>
            ) : null}
            {po.supplier?.trn || po.supplierTrn ? (
              <Text style={poPdfStyles.companyDetails}>TRN: {po.supplier?.trn || po.supplierTrn}</Text>
            ) : null}
            {(po.supplier?.city || po.supplierCity || po.supplier?.country || po.supplierCountry) && (
              <Text style={poPdfStyles.companyDetails}>
                {po.supplier?.city || po.supplierCity}{po.supplier?.country || po.supplierCountry ? `, ${po.supplier?.country || po.supplierCountry}` : ''}
              </Text>
            )}
            {po.supplier?.phone || po.supplierPhone ? (
              <Text style={poPdfStyles.companyDetails}>Phone: {po.supplier?.phone || po.supplierPhone}</Text>
            ) : null}
          </View>

          <View style={poPdfStyles.table}>
            <View style={poPdfStyles.tableHeader}>
              <Text style={poPdfStyles.col1}>Description</Text>
              <Text style={poPdfStyles.col2}>Qty</Text>
              <Text style={poPdfStyles.col3}>Unit</Text>
              <Text style={poPdfStyles.col4}>Unit Price</Text>
              <Text style={poPdfStyles.col5}>VAT %</Text>
              <Text style={poPdfStyles.col6}>VAT Amount</Text>
              <Text style={poPdfStyles.col7}>Total</Text>
            </View>
            {lines.map((line, index) => {
              const quantity = parseFloat(line.quantity || 0);
              const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
              const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
              const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
              const afterDiscount = quantity * unitPrice - discount;
              const vat = afterDiscount * (vatPercent / 100);
              const totalLine = afterDiscount + vat;
              return (
                <View key={index} style={[poPdfStyles.tableRow, index % 2 === 1 ? poPdfStyles.tableRowAlt : {}]}>
                  <Text style={poPdfStyles.col1}>{line.description}</Text>
                  <Text style={poPdfStyles.col2}>{quantity}</Text>
                  <Text style={poPdfStyles.col3}>{line.unit || 'Unit'}</Text>
                  <Text style={poPdfStyles.col4}>{formatCurrencyPdf(unitPrice)}</Text>
                  <Text style={poPdfStyles.col5}>{vatPercent}%</Text>
                  <Text style={poPdfStyles.col6}>{formatCurrencyPdf(vat)}</Text>
                  <Text style={poPdfStyles.col7}>{formatCurrencyPdf(totalLine)}</Text>
                </View>
              );
            })}
          </View>

          <View style={poPdfStyles.totals}>
            <View style={poPdfStyles.totalRow}>
              <Text style={poPdfStyles.totalLabel}>Subtotal:</Text>
              <Text style={poPdfStyles.totalValue}>{formatCurrencyPdf(subtotal)}</Text>
            </View>
            <View style={poPdfStyles.totalRow}>
              <Text style={poPdfStyles.totalLabel}>Total Discount:</Text>
              <Text style={poPdfStyles.totalValue}>{formatCurrencyPdf(totalDiscount)}</Text>
            </View>
            <View style={poPdfStyles.totalRow}>
              <Text style={poPdfStyles.totalLabel}>Taxable Amount:</Text>
              <Text style={poPdfStyles.totalValue}>{formatCurrencyPdf(taxableAmount)}</Text>
            </View>

            <View style={poPdfStyles.summaryRow}>
              <View style={poPdfStyles.summaryBox}>
                <Text style={poPdfStyles.summaryLabel}>VAT Amount</Text>
                <Text style={poPdfStyles.summaryValue}>{formatCurrencyPdf(vatTotal)}</Text>
              </View>
              <View style={poPdfStyles.summaryBox}>
                <Text style={poPdfStyles.summaryLabel}>Total Amount</Text>
                <Text style={poPdfStyles.summaryValue}>{formatCurrencyPdf(total)}</Text>
              </View>
            </View>
          </View>

          {po.notes ? (
            <View style={poPdfStyles.notes}>
              <Text style={poPdfStyles.notesTitle}>Notes / Terms:</Text>
              <Text style={poPdfStyles.notesText}>{po.notes}</Text>
            </View>
          ) : null}

          <View style={poPdfStyles.footer}>
            <Text>This is a computer generated purchase invoice. No signature required.</Text>
            <Text style={poPdfStyles.compliance}>
              🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN: 104836477000001
            </Text>
          </View>
        </Page>
      </Document>
    );
  };

  const handleDownloadPdf = async (po) => {
    try {
      const pdfDocument = React.createElement(POPDFDocument, { po });
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PurchaseOrder-${po.po_no || po.poNo || po.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Unable to generate PDF. Please try again.');
    }
  };

  const POsTablePDFDocument = () => {
    const totalAmount = filteredPOs.reduce((sum, po) => sum + calculatePOTotal(po), 0);
    const totalVAT = filteredPOs.reduce((sum, po) => sum + calculatePOVAT(po), 0);

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={poPdfStyles.page}>
          <View style={{ padding: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, color: '#0891b2' }}>
              Purchase Invoice Listing Report
            </Text>
            <Text style={{ fontSize: 10, color: '#666', marginBottom: 10 }}>
              Generated on {formatDate(new Date().toISOString())}
            </Text>

            <View style={poPdfStyles.table}>
              <View style={poPdfStyles.tableHeader}>
                <Text style={{ width: '15%', paddingHorizontal: 5 }}>PO No</Text>
                <Text style={{ width: '25%', paddingHorizontal: 5 }}>Supplier</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center' }}>Date</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right' }}>Amount</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right' }}>VAT</Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center' }}>Status</Text>
              </View>

              {filteredPOs.map((po, index) => (
                <View key={index} style={[poPdfStyles.tableRow, index % 2 === 1 ? poPdfStyles.tableRowAlt : {}]}>
                  <Text style={{ width: '15%', paddingHorizontal: 5, fontSize: 10 }}>
                    {po.po_no || po.poNo}
                  </Text>
                  <Text style={{ width: '25%', paddingHorizontal: 5, fontSize: 10 }}>
                    {po.supplier?.name || po.supplierName || 'Unknown'}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center', fontSize: 10 }}>
                    {formatDate(po.po_date || po.date)}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10 }}>
                    {formatCurrencyPdf(calculatePOTotal(po))}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 10, color: '#b45309' }}>
                    {formatCurrencyPdf(calculatePOVAT(po))}
                  </Text>
                  <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'center', fontSize: 9 }}>
                    {po.status}
                  </Text>
                </View>
              ))}

              <View style={[poPdfStyles.tableRow, { backgroundColor: '#f3f4f6', fontWeight: 'bold' }]}>
                <Text style={{ width: '55%', paddingHorizontal: 5, fontSize: 11, fontWeight: 'bold', textAlign: 'right' }}>
                  GRAND TOTAL:
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold' }}>
                  {formatCurrencyPdf(totalAmount)}
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5, textAlign: 'right', fontSize: 11, fontWeight: 'bold', color: '#b45309' }}>
                  {formatCurrencyPdf(totalVAT)}
                </Text>
                <Text style={{ width: '15%', paddingHorizontal: 5 }}></Text>
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  const handleExportTablePdf = async () => {
    try {
      const pdfDocument = React.createElement(POsTablePDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase-orders-list-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // Pagination Component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
              currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
              currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
              <span className="font-medium">{Math.min(indexOfLastItem, filteredPOs.length)}</span> of{' '}
              <span className="font-medium">{filteredPOs.length}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0 ${
                  currentPage === 1 
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50' 
                    : 'text-gray-400 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => paginate(1)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    1
                  </button>
                  {startPage > 2 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                      ...
                    </span>
                  )}
                </>
              )}
              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0 ${
                    currentPage === number
                      ? 'z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {number}
                </button>
              ))}
              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                      ...
                    </span>
                  )}
                  <button
                    onClick={() => paginate(totalPages)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0 ${
                  currentPage === totalPages 
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50' 
                    : 'text-gray-400 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Purchase Invoices</h2>
          <p className="text-sm text-gray-600">Manage all your purchase invoices</p>
        </div>
        <Link to="/purchases/orders" className="btn btn-primary btn-sm sm:btn-md w-full sm:w-auto">
          ➕ Create Purchase Invoice
        </Link>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by PO No
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter PO number..."
              value={searchPONo}
              onChange={(e) => setSearchPONo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Supplier
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter supplier name..."
              value={searchSupplier}
              onChange={(e) => setSearchSupplier(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Site/Customer
            </label>
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              placeholder="Enter site/customer name..."
              value={searchSite}
              onChange={(e) => setSearchSite(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Status
            </label>
            <select
              className="select select-bordered select-sm w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
        </div>
        {(searchPONo || searchSupplier || searchSite || statusFilter !== 'All') && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {filteredPOs.length} of {purchaseOrders.length} purchase invoices
            </span>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => {
                setSearchPONo('');
                setSearchSupplier('');
                setSearchSite('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Loading purchase invoices from the server...
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* POs Table - Desktop View */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden hidden md:block">
        {currentItems.length > 0 && (
          <div className="p-4 border-b border-gray-200 flex justify-end">
            <button
              onClick={handleExportTablePdf}
              className="btn btn-sm btn-outline btn-primary"
            >
              📥 Export Table to PDF
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead className="bg-gray-50">
              <tr>
                <th>Sl</th>
                <th>Ref. No</th>
                <th>PO No</th>
                <th>Supplier</th>
                <th>Site</th>
                <th>Date</th>
                <th>Amount</th>
                <th>VAT</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((po, index) => (
                <tr key={po.id} className="hover:bg-gray-50">
                  <td className="font-medium text-blue-600">
                    {indexOfFirstItem + index + 1}
                  </td>
                  <td className="font-medium text-blue-600">
                    {po.reference_no}
                  </td>
                  <td className="font-medium text-primary">
                    {po.po_no || po.poNo}
                  </td>
                  <td>
                    {po.supplier?.name || po.supplierName || 'Unknown'}
                  </td>
                  <td>
                    {po.customer?.name || 'Unknown'}
                  </td>
                  <td>
                    {formatDate(po.po_date || po.date)}
                  </td>
                  <td className="font-mono">
                    {formatCurrency(calculatePOTotal(po))}
                  </td>
                  <td className="font-mono text-amber-600 font-medium">
                    {formatCurrency(calculatePOVAT(po))}
                  </td>
                  <td>
                    <span className={getStatusBadge(po.status)}>
                      {po.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => setPreviewPO(po)}
                      >
                        👁️ View
                      </button>
                      <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => handleDownloadPdf(po)}
                      >
                        📄 PDF
                      </button>
                      <button
                        className="btn btn-xs btn-ghost btn-info text-white"
                        onClick={() => {
                          console.log('🔗 Editing PO:', po);
                          console.log('🔗 PO ID:', po.id);
                          console.log('🔗 PO Details:', {
                            id: po.id,
                            po_no: po.po_no,
                            supplier: po.supplier,
                            status: po.status
                          });
                          navigate(`/purchases/invoices/edit/${po.id}`);
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn btn-xs btn-ghost btn-error text-white"
                        onClick={() => handleDelete(po.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              
              {/* Current Page Totals Row */}
              {currentItems.length > 0 && (
                <tr className="bg-blue-50 font-semibold border-t-2 border-blue-200">
                  <td colSpan="6" className="text-right pr-4 text-blue-800">Page Total:</td>
                  <td className="font-mono text-blue-800 font-bold">
                    {formatCurrency(currentPageTotalAmount)}
                  </td>
                  <td className="font-mono text-blue-800 font-bold">
                    {formatCurrency(currentPageTotalVAT)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              )}
              
              {/* GRAND TOTAL Row */}
              {filteredPOs.length > 0 && (
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-400">
                  <td colSpan="6" className="text-right pr-4 text-lg">GRAND TOTAL:</td>
                  <td className="font-mono text-gray-900 text-lg font-bold">
                    {formatCurrency(grandTotalAmount)}
                  </td>
                  <td className="font-mono text-amber-700 text-lg font-bold">
                    {formatCurrency(grandTotalVAT)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {currentItems.length === 0 && filteredPOs.length > 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📄</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items on this page</h3>
            <p className="text-gray-600 mb-4">Try navigating to another page</p>
          </div>
        )}

        {filteredPOs.length === 0 && purchaseOrders.length > 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching purchase invoices</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setSearchPONo('');
                setSearchSupplier('');
                setSearchSite('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {purchaseOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <span className="text-4xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No purchase invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first purchase invoice to get started</p>
            <Link to="/purchase-orders/create" className="btn btn-primary">
              Create Your First Purchase Invoice
            </Link>
          </div>
        )}

        {/* Pagination Component */}
        <Pagination />
      </div>

      {/* POs Cards - Mobile View */}
      <div className="md:hidden">
        <div className="space-y-3">
          {currentItems.length === 0 && filteredPOs.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm text-center py-12">
              <div className="text-gray-400 mb-4">
                <span className="text-4xl">📄</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No items on this page</h3>
              <p className="text-gray-600 mb-4">Try navigating to another page</p>
            </div>
          ) : filteredPOs.length === 0 && purchaseOrders.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm text-center py-12">
              <div className="text-gray-400 mb-4">
                <span className="text-4xl">🔍</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching purchase invoices</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search criteria</p>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setSearchPONo('');
                  setSearchSupplier('');
                  setSearchSite('');
                  setStatusFilter('All');
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : filteredPOs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm text-center py-12">
              <div className="text-gray-400 mb-4">
                <span className="text-4xl">📋</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No purchase invoices yet</h3>
              <p className="text-gray-600 mb-4">Create your first purchase invoice to get started</p>
              <Link to="/purchase-orders/create" className="btn btn-primary btn-sm">
                Create Your First Purchase Invoice
              </Link>
            </div>
          ) : (
            <>
              {currentItems.map((po) => (
                <div key={po.id} className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div>
                        <p className="font-medium text-primary text-sm">
                          {po.po_no || po.poNo}
                        </p>
                        <p className="text-gray-600 text-xs">
                          {po.supplier?.name || po.supplierName || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <span className={getStatusBadge(po.status)}>
                      {po.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    <div>
                      <p className="text-gray-500">Site</p>
                      <p className="font-medium">{po.customer?.name || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Date</p>
                      <p className="font-medium">{formatDate(po.po_date || po.date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Amount</p>
                      <p className="font-medium font-mono">{formatCurrency(calculatePOTotal(po))}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">VAT</p>
                      <p className="font-medium font-mono text-amber-600">{formatCurrency(calculatePOVAT(po))}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="btn btn-xs btn-ghost flex-1"
                      onClick={() => setPreviewPO(po)}
                    >
                      👁️ View
                    </button>
                    <button
                      className="btn btn-xs btn-ghost flex-1"
                      onClick={() => handleDownloadPdf(po)}
                    >
                      📄 PDF
                    </button>
                    <button 
                      className="btn btn-xs btn-ghost btn-error text-white flex-1"
                      onClick={() => handleDelete(po.id)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Mobile Totals Section */}
              {currentItems.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-blue-800">Page Total:</span>
                    <div className="text-right">
                      <div className="font-mono text-blue-800 font-bold">{formatCurrency(currentPageTotalAmount)}</div>
                      <div className="font-mono text-blue-800 text-sm">VAT: {formatCurrency(currentPageTotalVAT)}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {filteredPOs.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">GRAND TOTAL:</span>
                    <div className="text-right">
                      <div className="font-mono text-gray-900 text-lg font-bold">{formatCurrency(grandTotalAmount)}</div>
                      <div className="font-mono text-amber-700 text-sm font-semibold">VAT: {formatCurrency(grandTotalVAT)}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Mobile Pagination */}
        {filteredPOs.length > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className={`btn btn-sm ${
                currentPage === 1 ? 'btn-disabled' : 'btn-primary'
              }`}
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className={`btn btn-sm ${
                currentPage === totalPages ? 'btn-disabled' : 'btn-primary'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {previewPO && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/10">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold">Purchase Invoice Preview</h3>
                <p className="text-sm text-gray-500">
                  {previewPO.po_no || previewPO.poNo}
                </p>
              </div>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setPreviewPO(null)}
              >
                Close
              </button>
            </div>

            <div className="space-y-6 p-6 overflow-y-auto max-h-[85vh]">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">PO Number</p>
                  <p className="font-semibold text-gray-900">
                    {previewPO.po_no || previewPO.poNo}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Date</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(previewPO.po_date || previewPO.date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                  <span className={getStatusBadge(previewPO.status)}>
                    {previewPO.status}
                  </span>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Supplier</p>
                  <p className="font-semibold text-gray-900">
                    {previewPO.supplier?.name || previewPO.supplierName || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewPO.supplier?.phone || previewPO.supplierPhone || ''}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewPO.supplier?.address || previewPO.supplierAddress || ''}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Site/Customer</p>
                  <p className="font-semibold text-gray-900">
                    {previewPO.customer?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewPO.customer?.phone || ''}
                  </p>
                  <p className="text-sm text-gray-600">
                    {previewPO.customer?.address || ''}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Amount</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(calculatePOTotal(previewPO))}</p>
                  <p className="text-sm text-gray-600">VAT: {formatCurrency(calculatePOVAT(previewPO))}</p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">VAT %</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(previewPO.plines || previewPO.items || []).map((line, lineIndex) => {
                      const quantity = parseFloat(line.quantity || 0);
                      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
                      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
                      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
                      const subtotal = quantity * unitPrice;
                      const afterDiscount = subtotal - discount;
                      const vat = afterDiscount * (vatPercent / 100);
                      const total = afterDiscount + vat;

                      return (
                        <tr key={lineIndex}>
                          <td className="px-4 py-3 text-sm text-gray-700">{line.description}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{quantity}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(unitPrice)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(discount)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-700">{vatPercent}%</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-4 justify-end md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {formatCurrency(calculatePOTotal(previewPO))}
                  </p>
                </div>
                <div className="rounded-2xl bg-gray-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Notes</p>
                  <p className="mt-2 text-sm text-gray-700">{previewPO.notes || 'No notes provided'}</p>
                </div>
              </div>

              {previewPO.image && (
                <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleImagePreview(previewPO.image)}
                      >
                        👁️ Preview
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleImageDownload(previewPO.image)}
                      >
                        📥 Download
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {imagePreviewUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="max-w-4xl w-full flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Image Preview</h3>
              <button
                className="btn btn-sm btn-ghost text-white hover:bg-white/20"
                onClick={() => setImagePreviewUrl(null)}
              >
                ✕ Close
              </button>
            </div>
            <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center max-h-[80vh]">
              <img
                src={imagePreviewUrl}
                alt="PO Attachment Preview"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="text-white p-8 text-center"><p>Unable to load image</p></div>';
                }}
              />
            </div>
            <div className="flex gap-2">
              <a
                href={imagePreviewUrl}
                download
                className="btn btn-sm btn-primary flex-1"
              >
                📥 Download Image
              </a>
              <button
                className="btn btn-sm btn-ghost flex-1 text-white hover:bg-white/20"
                onClick={() => window.open(imagePreviewUrl, '_blank')}
              >
                🔗 Open in New Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseInvoiceList;